import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import {Body2, Display3, Headline1} from '@adv-mt/ui/typography'

import PriceIndicator from '../price/PriceIndicator.js'
import {BASE_CLASS} from './config.js'
import RentingOptions from './RentingOptions.js'
import {getSelectedOption} from './utils.js'

const RentingPrice = ({ad = {}, badges, onSelectionChange, onOpenDrawer}) => {
  const {i18n, domain} = useContext(Context)

  const {monthlySubscriptionFee, subscriptionPriceOptions = []} = ad

  // Holds the shape the user picked, not the resolved option, so the selection survives a target buyer switch
  const [selection, setSelection] = useState({})

  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !domain.get('config').get('isMobile')
  })

  const Fee = isHigherThanMobile ? Display3 : Headline1

  const selectedOption = getSelectedOption({...selection, monthlySubscriptionFee, subscriptionPriceOptions})
  const selectedCommitmentMonths = selectedOption?.totalMonths
  const selectedKmAllowance = selectedOption && {
    kmIncluded: selectedOption.kmIncluded,
    kmIncludedPeriod: selectedOption.kmIncludedPeriod
  }

  const handleCommitmentMonthsSelected = commitmentMonths =>
    setSelection({commitmentMonths, kmAllowance: selectedKmAllowance})
  const handleKmAllowanceSelected = kmAllowance =>
    setSelection({commitmentMonths: selectedCommitmentMonths, kmAllowance})

  const selectedMonthlyFee = selectedOption?.monthlyPrice ?? monthlySubscriptionFee

  // The page needs the whole option, not just the fee: a subscription lead must carry
  // `preferences.subscription.commitment`, and only this component knows which option
  // is selected.
  useEffect(() => {
    onSelectionChange?.({monthlyFee: selectedMonthlyFee, commitment: selectedOption})
  }, [onSelectionChange, selectedMonthlyFee, selectedOption])

  return (
    <div className={BASE_CLASS}>
      <div className={`${BASE_CLASS}-priceColumn`}>
        <div className={`${BASE_CLASS}-fee`}>
          <div>
            <Body2 className={`${BASE_CLASS}-feeLabel`}>{i18n.t('COMMON.FEE')}</Body2>
            <Fee as="p" className={`${BASE_CLASS}-feeValue`}>
              {i18n.c(selectedMonthlyFee)}
              <Body2 as="span" highlight>
                {i18n.t('COMMON.PERIOD.MONTHLY')}
              </Body2>
            </Fee>
          </div>
          <PriceIndicator ad={ad} onOpenDrawer={onOpenDrawer} />
        </div>

        {badges}
      </div>

      {subscriptionPriceOptions.length > 0 && (
        <RentingOptions
          onCommitmentMonthsSelected={handleCommitmentMonthsSelected}
          onKmAllowanceSelected={handleKmAllowanceSelected}
          selectedCommitmentMonths={selectedCommitmentMonths}
          selectedKmAllowance={selectedKmAllowance}
          subscriptionPriceOptions={subscriptionPriceOptions}
        />
      )}
    </div>
  )
}

RentingPrice.displayName = 'RentingPrice'
RentingPrice.propTypes = {
  ad: PropTypes.object,
  badges: PropTypes.node,
  onSelectionChange: PropTypes.func,
  onOpenDrawer: PropTypes.func.isRequired
}

export default RentingPrice
